// The right-click menu: one at a time, opened at the pointer with openContextMenu().
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { createStore, useStore } from "../store";
import { Icon } from "./Icon";
import type { MenuItem } from "./ui";

type Open = { x: number; y: number; items: MenuItem[] };

const contextMenu = createStore<{ open: Open | null }>({ open: null });

/**
 * Shows `items` at the pointer instead of the browser's menu. Shift + right-click still opens
 * the browser's own menu, for copying text or opening links.
 */
export const openContextMenu = (event: MouseEvent, items: MenuItem[]) => {
  if (event.shiftKey || items.length === 0) return;
  event.preventDefault();
  event.stopPropagation();
  contextMenu.set({ open: { x: event.clientX, y: event.clientY, items } });
};

export const closeContextMenu = () => contextMenu.set({ open: null });

/** Keeps a panel inside the window, flipping it to the other side of its anchor if needed. */
const fit = (panel: HTMLElement, x: number, y: number, flipX: number) => {
  const rect = panel.getBoundingClientRect();
  const margin = 8;
  const left =
    x + rect.width + margin > window.innerWidth ? Math.max(margin, flipX - rect.width) : x;
  const top = Math.max(margin, Math.min(y, window.innerHeight - rect.height - margin));
  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
  panel.style.visibility = "visible";
};

const Panel = ({
  items,
  x,
  y,
  flipX,
  onDone,
  autoFocus,
  onBack,
}: {
  items: MenuItem[];
  x: number;
  y: number;
  flipX: number;
  onDone: () => void;
  autoFocus: boolean;
  onBack?: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [sub, setSub] = useState<{ index: number; focus: boolean } | null>(null);
  const [subAt, setSubAt] = useState({ x: 0, y: 0, flipX: 0 });
  const nested = onBack !== undefined;
  useLayoutEffect(() => {
    if (!ref.current) return;
    fit(ref.current, x, y, flipX);
    if (autoFocus) ref.current.querySelector<HTMLElement>(":scope > button")?.focus();
    // The top menu takes focus itself, so arrow keys work without marking an item for mouse users.
    else if (!nested) ref.current.focus();
  }, [x, y, flipX, autoFocus, nested]);

  const openSub = (index: number, button: HTMLElement, focus: boolean) => {
    const rect = button.getBoundingClientRect();
    setSubAt({ x: rect.right - 4, y: rect.top - 6, flipX: rect.left + 4 });
    setSub({ index, focus });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const buttons = [
      ...(ref.current?.querySelectorAll<HTMLButtonElement>(":scope > button") ?? []),
    ];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (index === -1) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        buttons[event.key === "ArrowDown" ? 0 : buttons.length - 1]?.focus();
      }
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      buttons[(index + step + buttons.length) % buttons.length]?.focus();
    } else if (event.key === "ArrowRight") {
      const button = buttons[index];
      const itemIndex = Number(button?.dataset.index);
      if (button?.dataset.submenu) {
        event.preventDefault();
        openSub(itemIndex, button, true);
      }
    } else if (event.key === "ArrowLeft" && onBack) {
      event.preventDefault();
      onBack();
    }
  };

  const subItems = (() => {
    if (!sub) return null;
    const item = items[sub.index];
    if (!item || !("submenu" in item)) return null;
    return typeof item.submenu === "function" ? item.submenu() : item.submenu;
  })();

  return (
    <>
      <div
        class="menu context-menu"
        role="menu"
        ref={ref}
        tabIndex={-1}
        style={{ left: `${x}px`, top: `${y}px`, visibility: "hidden" }}
        onKeyDown={onKeyDown}
        onContextMenu={(event) => event.preventDefault()}
      >
        {items.map((item, index) => {
          if ("separator" in item) return <hr key={`s${index}`} />;
          if ("heading" in item)
            return (
              <div class="menu-heading" key={`h${index}`}>
                {item.heading}
              </div>
            );
          const lead = item.color ? (
            <span class="dot" style={{ background: item.color }} />
          ) : item.icon ? (
            <Icon name={item.icon} size={16} />
          ) : (
            <span class="menu-spacer" />
          );
          if ("submenu" in item)
            return (
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={sub?.index === index}
                key={`${item.label}${index}`}
                class={sub?.index === index ? "open" : ""}
                data-index={index}
                data-submenu="1"
                onMouseEnter={(event) => openSub(index, event.currentTarget, false)}
                onClick={(event) => openSub(index, event.currentTarget, true)}
              >
                {lead}
                <span>{item.label}</span>
                <Icon name="chevronRight" size={14} class="menu-arrow" />
              </button>
            );
          return (
            <button
              type="button"
              role="menuitem"
              key={`${item.label}${index}`}
              class={item.danger ? "danger" : ""}
              data-index={index}
              onMouseEnter={() => setSub(null)}
              onClick={() => {
                onDone();
                item.onSelect();
              }}
            >
              {lead}
              <span>{item.label}</span>
              {item.checked && <Icon name="check" size={16} class="menu-check" />}
            </button>
          );
        })}
      </div>
      {sub && subItems && (
        <Panel
          key={sub.index}
          items={subItems.length > 0 ? subItems : [{ heading: "Nothing here yet" }]}
          x={subAt.x}
          y={subAt.y}
          flipX={subAt.flipX}
          onDone={onDone}
          autoFocus={sub.focus}
          onBack={() => {
            const index = sub.index;
            setSub(null);
            ref.current?.querySelector<HTMLElement>(`:scope > [data-index="${index}"]`)?.focus();
          }}
        />
      )}
    </>
  );
};

/** Mounted once at the root of the app. */
export const ContextMenu = () => {
  const open = useStore(contextMenu, (state) => state.open);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) closeContextMenu();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeContextMenu();
        previous?.focus?.();
      }
    };
    const close = () => closeContextMenu();
    const onScroll = (event: Event) => {
      if (!root.current?.contains(event.target as Node)) closeContextMenu();
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);
  if (!open) return null;
  return (
    <div ref={root}>
      <Panel
        key={`${open.x},${open.y}`}
        items={open.items}
        x={open.x}
        y={open.y}
        flipX={open.x}
        onDone={closeContextMenu}
        autoFocus={false}
      />
    </div>
  );
};
